import { useEffect } from "react";

import PropTypes from "prop-types";
import { Controller, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormHelperText,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { CustomDialogTitle, CustomNumberField, CustomSelect } from "../../ui";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./lens-product-variant-group-form-constants";

export const LensProductVariantGroupForm = ({
  hasAdd,
  existingGroups,
  defaultValues,
  onSubmit,
  onClose,
}) => {
  const { hasPermission } = useAuth();
  const canUpdateRetailPrice = hasPermission(
    PermissionEnum.UPDATE_PRODUCT_RETAIL_PRICE
  );
  const canUpdateDistributorPrice = hasPermission(
    PermissionEnum.UPDATE_PRODUCT_DISTRIBUTOR_PRICE
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(
      VALIDATION_SCHEMA(
        hasAdd,
        existingGroups,
        canUpdateRetailPrice,
        canUpdateDistributorPrice
      )
    ),
  });

  useEffect(() => {
    if (defaultValues) reset({ ...DEFAULT_VALUES, ...defaultValues });
  }, [defaultValues, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onFormSubmit = (data) => {
    onSubmit(data);
    onClose();
  };

  return (
    <Dialog open component="form" onClose={handleClose}>
      <CustomDialogTitle
        variant="h5"
        title="Grup Varian Produk"
        onClose={handleClose}
      />
      <DialogContent>
        <Grid container spacing={2}>
          <Grid size={4}>
            <Controller
              control={control}
              name="sphStart"
              render={({ field, fieldState }) => (
                <CustomNumberField
                  value={field.value}
                  onChange={field.onChange}
                  inputProps={{
                    allowNegative: true,
                  }}
                  required
                  label="Sph Awal"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid size={4}>
            <Controller
              control={control}
              name="sphEnd"
              render={({ field, fieldState }) => (
                <CustomNumberField
                  value={field.value}
                  onChange={field.onChange}
                  inputProps={{
                    allowNegative: true,
                  }}
                  required
                  label="Sph Akhir"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid size={4}>
            <Controller
              control={control}
              name="stepSph"
              render={({ field, fieldState }) => (
                <CustomSelect
                  required
                  id="stepSph"
                  label="Step Sph"
                  options={[
                    { value: 0.25, label: "0.25" },
                    { value: 0.5, label: "0.50" },
                    { value: 1.0, label: "1.00" },
                  ]}
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid size={6}>
            <Controller
              control={control}
              name="cylStart"
              render={({ field, fieldState }) => (
                <CustomNumberField
                  value={field.value}
                  onChange={field.onChange}
                  inputProps={{
                    allowNegative: true,
                  }}
                  required
                  label="Cyl Awal"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid size={6}>
            <Controller
              control={control}
              name="cylEnd"
              render={({ field, fieldState }) => (
                <CustomNumberField
                  value={field.value}
                  onChange={field.onChange}
                  inputProps={{
                    allowNegative: true,
                  }}
                  required
                  label="Cyl Akhir"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          {hasAdd && (
            <>
              <Grid size={6}>
                <Controller
                  control={control}
                  name="addStart"
                  render={({ field, fieldState }) => (
                    <CustomNumberField
                      value={field.value}
                      onChange={field.onChange}
                      required
                      label="Add Awal"
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={6}>
                <Controller
                  control={control}
                  name="addEnd"
                  render={({ field, fieldState }) => (
                    <CustomNumberField
                      value={field.value}
                      onChange={field.onChange}
                      required
                      label="Add Akhir"
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
            </>
          )}
          <Grid size={{ xs: 6, md: 2 }}>
            <Controller
              control={control}
              name="combination"
              render={({ field, fieldState }) => (
                <CustomNumberField
                  value={field.value}
                  onChange={field.onChange}
                  inputProps={{
                    allowNegative: true,
                    placeholder: "-6",
                  }}
                  label="Comb"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          {canUpdateRetailPrice && (
            <Grid size={{ xs: 6, md: 5 }}>
              <Controller
                control={control}
                name="retailPrice"
                render={({ field, fieldState }) => (
                  <CustomNumberField
                    value={field.value}
                    onChange={field.onChange}
                    required
                    label="Harga Retail (1 Pair)"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
          )}
          {canUpdateDistributorPrice && (
            <Grid size={{ xs: 6, md: 5 }}>
              <Controller
                control={control}
                name="distributorPrice"
                render={({ field, fieldState }) => (
                  <CustomNumberField
                    value={field.value}
                    onChange={field.onChange}
                    required
                    label="Harga Distributor (1 Pair)"
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
        {errors.overlapValidation && (
          <FormHelperText error sx={{ mt: 2 }}>
            Error Validasi: {errors.overlapValidation.message}
          </FormHelperText>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleSubmit(onFormSubmit)}>
          Simpan
        </Button>
      </DialogActions>
    </Dialog>
  );
};

LensProductVariantGroupForm.propTypes = {
  hasAdd: PropTypes.bool,
  existingGroups: PropTypes.arrayOf(PropTypes.object),
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
